import React from 'react';
import { 
    Typography, 
    Box, 
    Button, 
    Paper, 
    TextField, 
    IconButton, 
    Divider,
    FormControlLabel,
    Switch
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useLeakCheckForm } from './LeakCheckFormContext';
import { CustomFieldsList } from './CustomFieldsList';

export const CustomSectionsPanel: React.FC = () => {
    const { 
        form, 
        addCustomSection, 
        removeCustomSection, 
        updateCustomSection,
        reorderCustomSections
    } = useLeakCheckForm();
    
    if (!form) return null;

    // Handle drag and drop of custom sections
    const onDragEnd = (result: any) => {
        if (!result.destination || !form) return;

        const sections = Array.from(form.customSections);
        const [removed] = sections.splice(result.source.index, 1);
        sections.splice(result.destination.index, 0, removed);

        reorderCustomSections(sections);
    };
    
    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                    Custom Form Sections
                </Typography>
                <Button 
                    startIcon={<AddIcon />}
                    color="primary"
                    onClick={addCustomSection}
                    variant="contained"
                >
                    Add Custom Section
                </Button>
            </Box>

            {form.customSections.length === 0 ? (
                <Paper variant="outlined">
                    <Box p={3} textAlign="center">
                        <Typography color="textSecondary">
                            No custom sections added. Click "Add Custom Section" to create a section.
                        </Typography>
                    </Box>
                </Paper>
            ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="custom-sections">
                        {(provided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {form.customSections
                                    .sort((a, b) => a.order - b.order)
                                    .map((section, idx) => (
                                    <Draggable 
                                        key={section.id} 
                                        draggableId={section.id} 
                                        index={idx}
                                    >
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                            >
                                                <Box mb={3}>
                                                    <Paper variant="outlined">
                                                        <Box p={2}>
                                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                                <Box display="flex" alignItems="center">
                                                                    <div {...provided.dragHandleProps}>
                                                                        <DragHandleIcon color="action" style={{ marginRight: 8 }} />
                                                                    </div>
                                                                    <TextField
                                                                        label="Section Name"
                                                                        value={section.name}
                                                                        onChange={(e) => updateCustomSection(idx, { name: e.target.value })}
                                                                        variant="outlined"
                                                                        size="small"
                                                                        style={{ minWidth: 250 }}
                                                                    />
                                                                </Box>
                                                                <Box>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={section.multipleRows}
                                                                                onChange={(e) => updateCustomSection(idx, { multipleRows: e.target.checked })}
                                                                                color="primary"
                                                                                size="small"
                                                                            />
                                                                        }
                                                                        label="Allow Multiple Rows"
                                                                    />
                                                                    <IconButton 
                                                                        onClick={() => removeCustomSection(idx)}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                            
                                                            <Divider />
                                                            
                                                            <Box mt={2}>
                                                                <CustomFieldsList
                                                                    title="Section Fields"
                                                                    fields={section.fields}
                                                                    sectionType="customSection"
                                                                    sectionIndex={idx}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}
        </>
    );
};